// DQRulesReport.tsx
// =================
// Drop-in DQ rules report component.
//
// Usage in your app:
//   import { DQRulesReport } from "./DQRulesReport";
//   <DQRulesReport entityId="0XX5FQEV" />
//
// The component has three views managed internally:
//   1. Summary  — health stats + cross-table rule cards
//   2. RuleList — full cross-table rule list (reached via "View all" button)
//   3. RuleDetail — deep-dive for one rule (reached by clicking a card)

import React, { useState, useEffect, useCallback } from "react";
import {
  getCrossDQSummary,
  getCrossTableRules,
  getDQRuleDetail,
  type DQCrossEntitySummary,
  type CrossTableRule,
  type DQRuleDetail,
  type SiblingRule,
  type RuleStatus,
  type Severity,
} from "../dqAPI";

// ─── Tailwind-free inline style tokens ───────────────────────────────────────
// All styles use CSS variables from your design system so they adapt to
// light / dark mode automatically with zero Tailwind dependency.

const css = {
  card: {
    background: "var(--color-background-primary)",
    border: "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-lg)",
    padding: "16px 20px",
  } as React.CSSProperties,
  secondaryCard: {
    background: "var(--color-background-secondary)",
    borderRadius: "var(--border-radius-md)",
    padding: "10px 14px",
  } as React.CSSProperties,
  sectionLabel: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: "var(--color-text-tertiary)",
    margin: "0 0 8px",
  } as React.CSSProperties,
  mono: {
    fontFamily: "var(--font-mono)",
    fontSize: 12,
  } as React.CSSProperties,
};

// ─── Small display helpers ────────────────────────────────────────────────────

const PREFIX_COLORS: Record<string, { bg: string; text: string }> = {
  FUNCTIONAL_LOCATION: { bg: "#E1F5EE", text: "#0F6E56" },
  EQUIPMENTS:          { bg: "#E6F1FB", text: "#185FA5" },
  WORK_ORDER:          { bg: "#FAEEDA", text: "#854F0B" },
};
const PREFIX_SHORT: Record<string, string> = {
  FUNCTIONAL_LOCATION: "FL",
  EQUIPMENTS:          "EQ",
  WORK_ORDER:          "WO",
};

function PrefixPill({ prefix }: { prefix: string }) {
  const color = PREFIX_COLORS[prefix] ?? { bg: "#F1EFE8", text: "#5F5E5A" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 500,
        background: color.bg,
        color: color.text,
      }}
    >
      {PREFIX_SHORT[prefix] ?? prefix}
    </span>
  );
}

const SEV_STYLE: Record<Severity, { bg: string; color: string }> = {
  H: { bg: "var(--color-background-danger)", color: "var(--color-text-danger)" },
  M: { bg: "var(--color-background-warning)", color: "var(--color-text-warning)" },
  L: { bg: "var(--color-background-secondary)", color: "var(--color-text-secondary)" },
};
const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  PASS:    { bg: "var(--color-background-success)", color: "var(--color-text-success)" },
  FAIL:    { bg: "var(--color-background-danger)",  color: "var(--color-text-danger)" },
  ERROR:   { bg: "var(--color-background-warning)", color: "var(--color-text-warning)" },
  NO_OP:   { bg: "var(--color-background-secondary)", color: "var(--color-text-tertiary)" },
  APPLIED: { bg: "var(--color-background-success)", color: "var(--color-text-success)" },
  NOT_RUN: { bg: "var(--color-background-secondary)", color: "var(--color-text-tertiary)" },
};

function Badge({
  label,
  style,
}: {
  label: string;
  style: { bg: string; color: string };
}) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.6,
        background: style.bg,
        color: style.color,
      }}
    >
      {label}
    </span>
  );
}

function SevBadge({ sev, act }: { sev: Severity; act: string }) {
  return <Badge label={`${sev} · ${act}`} style={SEV_STYLE[sev] ?? SEV_STYLE.L} />;
}
function StatusBadge({ status }: { status: RuleStatus | string }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.NOT_RUN;
  const icons: Record<string, string> = { PASS: "✓", FAIL: "✗", ERROR: "⚠" };
  return <Badge label={`${icons[status] ?? ""}${icons[status] ? " " : ""}${status}`} style={s} />;
}

function PassRate({ fail, total }: { fail: number; total: number }) {
  if (total === 0) return null;
  const pct = Math.round(((total - fail) / total) * 100);
  return (
    <div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: "var(--color-background-secondary)",
          overflow: "hidden",
          margin: "4px 0 2px",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: fail === 0 ? "#1D9E75" : "#E24B4A",
            borderRadius: 3,
          }}
        />
      </div>
      <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
        {total.toLocaleString()} checked · {fail.toLocaleString()} failed
      </div>
    </div>
  );
}

// ─── Stat card row ────────────────────────────────────────────────────────────

function StatCard({
  value,
  label,
  color,
}: {
  value: number | string;
  label: string;
  color?: string;
}) {
  return (
    <div style={{ ...css.secondaryCard, textAlign: "center" }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          lineHeight: 1.2,
          color: color ?? "var(--color-text-primary)",
        }}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

// ─── Cross-table rule card ────────────────────────────────────────────────────

function CrossRuleCard({
  rule,
  onClick,
}: {
  rule: CrossTableRule;
  onClick: (rule: CrossTableRule) => void;
}) {
  return (
    <div
      onClick={() => onClick(rule)}
      style={{
        ...css.card,
        cursor: "pointer",
        transition: "border-color 0.15s",
        borderColor:
          rule.status === "FAIL"
            ? "var(--color-border-danger)"
            : "var(--color-border-tertiary)",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor =
          "var(--color-border-primary)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor =
          rule.status === "FAIL"
            ? "var(--color-border-danger)"
            : "var(--color-border-tertiary)";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 6,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-text-primary)",
              marginBottom: 3,
              lineHeight: 1.4,
            }}
          >
            {rule.d}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              lineHeight: 1.5,
            }}
          >
            {rule.bi}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <StatusBadge status={rule.status} />
          <SevBadge sev={rule.sev} act={rule.act} />
          <span
            style={{
              fontSize: 11,
              color: "var(--color-text-tertiary)",
              ...css.mono,
            }}
          >
            {rule.fn}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: rule.total_records > 0 ? 6 : 0,
          flexWrap: "wrap",
        }}
      >
        {rule.prefixes.map((p, i) => (
          <React.Fragment key={p}>
            <PrefixPill prefix={p} />
            {i < rule.prefixes.length - 1 && (
              <span style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>↔</span>
            )}
          </React.Fragment>
        ))}
        <span
          style={{
            marginLeft: "auto",
            fontSize: 11,
            color: "var(--color-text-tertiary)",
          }}
        >
          score {rule.score}/10
        </span>
      </div>

      {rule.total_records > 0 && (
        <PassRate fail={rule.fail_count} total={rule.total_records} />
      )}
    </div>
  );
}

// ─── Rule detail view ─────────────────────────────────────────────────────────

function RuleDetailView({
  detail,
  onClose,
  onSiblingClick,
}: {
  detail: DQRuleDetail;
  onClose: () => void;
  onSiblingClick: (ruleId: string) => void;
}) {
  const { rule, sibling_rules, column_stats } = detail;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--color-text-secondary)",
          fontSize: 13,
          padding: "0 0 16px",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        ← Back to cross-table rules
      </button>

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 500,
              color: "var(--color-text-primary)",
              marginBottom: 4,
              lineHeight: 1.4,
            }}
          >
            {rule.d}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-tertiary)",
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={css.mono}>{rule.id}</span>
            {rule.prefixes.map((p) => (
              <PrefixPill key={p} prefix={p} />
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 5,
            flexShrink: 0,
          }}
        >
          <StatusBadge status={rule.status} />
          <SevBadge sev={rule.sev} act={rule.act} />
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
            score {rule.score}/10
          </span>
          {rule.lifecycle && (
            <Badge
              label={rule.lifecycle}
              style={{
                bg: "var(--color-background-secondary)",
                color: "var(--color-text-tertiary)",
              }}
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <StatCard
          value={rule.fail_count}
          label="violations"
          color={
            rule.fail_count > 0
              ? "var(--color-text-danger)"
              : "var(--color-text-success)"
          }
        />
        <StatCard value={rule.passed_count} label="passed" />
        <StatCard value={rule.total_records} label="checked" />
      </div>

      {rule.total_records > 0 && (
        <div style={{ marginBottom: 20 }}>
          <PassRate fail={rule.fail_count} total={rule.total_records} />
        </div>
      )}

      {/* DSL */}
      <div style={{ marginBottom: 20 }}>
        <p style={css.sectionLabel}>Rule expression</p>
        <div style={{ ...css.secondaryCard, ...css.mono }}>
          {rule.fn}(
          {rule.ve?.args?.map((a) => JSON.stringify(a)).join(", ")})
        </div>
        {rule.vl && (
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text-secondary)",
              marginTop: 6,
              fontStyle: "italic",
            }}
          >
            {rule.vl}
          </div>
        )}
      </div>

      {/* Business impact + fix */}
      <div style={{ ...css.card, marginBottom: 16 }}>
        <div style={{ marginBottom: 10 }}>
          <p style={css.sectionLabel}>Business impact</p>
          <p
            style={{
              fontSize: 13,
              color: "var(--color-text-primary)",
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            {rule.bi}
          </p>
        </div>
        {rule.fix && (
          <div
            style={{
              borderTop: "0.5px solid var(--color-border-tertiary)",
              paddingTop: 10,
            }}
          >
            <p style={css.sectionLabel}>Recommended fix</p>
            <p
              style={{
                fontSize: 13,
                color: "var(--color-text-secondary)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {rule.fix}
            </p>
          </div>
        )}
      </div>

      {/* Sample failures */}
      {rule.sample_failures.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <p style={css.sectionLabel}>
            Sample failures ({rule.fail_count} total)
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            {rule.sample_failures.map((sf, i) => (
              <div
                key={i}
                style={{
                  ...css.secondaryCard,
                  borderLeft: "3px solid var(--color-border-danger)",
                  borderRadius: "0 var(--border-radius-md) var(--border-radius-md) 0",
                }}
              >
                {sf.value_checked && (
                  <div style={{ ...css.mono, color: "var(--color-text-primary)", marginBottom: 4 }}>
                    {Object.entries(sf.value_checked)
                      .map(([k, v]) => `${k} = "${v}"`)
                      .join("  ·  ")}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--color-text-secondary)",
                    lineHeight: 1.5,
                  }}
                >
                  {sf.failure_reason ?? sf.error}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Examples */}
      {rule.exv && rule.exv.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <p style={css.sectionLabel}>Example values flagged</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {rule.exv.map((v, i) => (
              <span key={i} style={{ ...css.secondaryCard, ...css.mono }}>
                {v}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Rule note */}
      {rule.n && (
        <div
          style={{
            ...css.secondaryCard,
            borderLeft: "3px solid var(--color-border-secondary)",
            borderRadius: "0 var(--border-radius-md) var(--border-radius-md) 0",
            marginBottom: 20,
            fontSize: 12,
            color: "var(--color-text-secondary)",
            lineHeight: 1.5,
          }}
        >
          {rule.n}
        </div>
      )}

      {/* Sibling rules on same column */}
      {sibling_rules.length > 0 && (
        <div>
          <p style={css.sectionLabel}>
            All rules on {rule.anchor_col} ({column_stats.total_rules_on_col}{" "}
            rules, {column_stats.fail_count_on_col} failing)
          </p>
          <div style={{ display: "grid", gap: 6 }}>
            {sibling_rules.map((s) => (
              <div
                key={s.id}
                onClick={() => onSiblingClick(s.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  background: "var(--color-background-secondary)",
                  borderRadius: "var(--border-radius-md)",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "var(--color-background-primary)";
                  (e.currentTarget as HTMLDivElement).style.border =
                    "0.5px solid var(--color-border-secondary)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background =
                    "var(--color-background-secondary)";
                  (e.currentTarget as HTMLDivElement).style.border = "none";
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {s.d}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--color-text-tertiary)",
                      ...css.mono,
                      marginTop: 2,
                    }}
                  >
                    {s.id}
                  </div>
                </div>
                <StatusBadge status={s.status} />
                {s.fail_count > 0 && (
                  <span
                    style={{ fontSize: 11, color: "var(--color-text-danger)" }}
                  >
                    {s.fail_count.toLocaleString()} failed
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Summary view ─────────────────────────────────────────────────────────────

function SummaryView({
  summary,
  onRuleClick,
  onViewAll,
}: {
  summary: DQCrossEntitySummary;
  onRuleClick: (rule: CrossTableRule) => void;
  onViewAll: () => void;
}) {
  // Group cross-table rules by pair
  const byPair = new Map<string, CrossTableRule[]>();
  for (const rule of summary.cross_table_rules) {
    const key = rule.prefixes.join("__");
    if (!byPair.has(key)) byPair.set(key, []);
    byPair.get(key)!.push(rule);
  }

  return (
    <div>
      {/* Top stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          marginBottom: 24,
        }}
      >
        <StatCard value={summary.total_rules} label="total rules" />
        <StatCard
          value={summary.pass}
          label="passing"
          color="var(--color-text-success)"
        />
        <StatCard
          value={summary.fail}
          label="failing"
          color="var(--color-text-danger)"
        />
        <StatCard
          value={summary.cross_table_rule_count}
          label="cross-table"
          color="var(--color-text-info)"
        />
      </div>

      {/* Cross-table rules grouped by pair */}
      {Array.from(byPair.entries()).map(([pairKey, rules]) => {
        const prefixes = rules[0].prefixes;
        return (
          <div key={pairKey} style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              {prefixes.map((p, i) => (
                <React.Fragment key={p}>
                  <PrefixPill prefix={p} />
                  {i < prefixes.length - 1 && (
                    <span
                      style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}
                    >
                      ↔
                    </span>
                  )}
                </React.Fragment>
              ))}
              <span
                style={{
                  marginLeft: 4,
                  fontSize: 12,
                  color: "var(--color-text-tertiary)",
                }}
              >
                {rules.length} rule{rules.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {rules.map((rule) => (
                <CrossRuleCard key={rule.id} rule={rule} onClick={onRuleClick} />
              ))}
            </div>
          </div>
        );
      })}

      {/* View all */}
      <button
        onClick={onViewAll}
        style={{
          marginTop: 8,
          padding: "8px 16px",
          border: "0.5px solid var(--color-border-secondary)",
          borderRadius: "var(--border-radius-md)",
          background: "none",
          color: "var(--color-text-secondary)",
          fontSize: 13,
          cursor: "pointer",
          width: "100%",
        }}
      >
        View all {summary.total_rules} rules →
      </button>
    </div>
  );
}

// ─── Full rule list view ──────────────────────────────────────────────────────

function RuleListView({
  entityId,
  onRuleClick,
  onBack,
}: {
  entityId: string;
  onRuleClick: (rule: CrossTableRule) => void;
  onBack: () => void;
}) {
  const [rules, setRules] = useState<CrossTableRule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCrossTableRules(entityId)
      .then(setRules)
      .finally(() => setLoading(false));
  }, [entityId]);

  if (loading) return <LoadingState />;

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--color-text-secondary)",
          fontSize: 13,
          padding: "0 0 16px",
        }}
      >
        ← Back to summary
      </button>
      <p style={{ ...css.sectionLabel, marginBottom: 16 }}>
        All cross-table rules ({rules.length})
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {rules.map((rule) => (
          <CrossRuleCard key={rule.id} rule={rule} onClick={onRuleClick} />
        ))}
      </div>
    </div>
  );
}

// ─── Loading / error states ───────────────────────────────────────────────────

function LoadingState() {
  return (
    <div
      style={{
        padding: "32px 0",
        textAlign: "center",
        color: "var(--color-text-tertiary)",
        fontSize: 13,
      }}
    >
      Loading…
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: "24px",
        background: "var(--color-background-danger)",
        borderRadius: "var(--border-radius-md)",
        color: "var(--color-text-danger)",
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}

// ─── Root component ───────────────────────────────────────────────────────────

type View = "summary" | "list" | "detail";

interface DQRulesReportProps {
  entityId: string;
  /** Optional: jump straight to a specific rule detail on mount */
  initialRuleId?: string;
  /** Optional wrapper style override */
  style?: React.CSSProperties;
}

export function DQRulesReport({
  entityId,
  initialRuleId,
  style,
}: DQRulesReportProps) {
  const [view, setView]       = useState<View>(initialRuleId ? "detail" : "summary");
  const [summary, setSummary] = useState<DQCrossEntitySummary | null>(null);
  const [detail, setDetail]   = useState<DQRuleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Load summary on mount
  useEffect(() => {
    setLoading(true);
    getCrossDQSummary(entityId)
      .then(setSummary)
      .catch((e) => setError(e.message ?? "Failed to load DQ cross-entity summary"))
      .finally(() => setLoading(false));
  }, [entityId]);

  // Load initial rule if specified
  useEffect(() => {
    if (!initialRuleId) return;
    getDQRuleDetail(entityId, initialRuleId)
      .then((d) => {
        setDetail(d);
        setView("detail");
      })
      .catch(() => {/* fall back to summary */});
  }, [entityId, initialRuleId]);

  const openRule = useCallback(
    (rule: CrossTableRule) => {
      setLoading(true);
      getDQRuleDetail(entityId, rule.id)
        .then((d) => {
          setDetail(d);
          setView("detail");
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [entityId]
  );

  const openRuleById = useCallback(
    (ruleId: string) => {
      setLoading(true);
      getDQRuleDetail(entityId, ruleId)
        .then((d) => {
          setDetail(d);
          setView("detail");
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    },
    [entityId]
  );

  if (error) return <ErrorState message={error} />;
  if (loading && view === "summary") return <LoadingState />;

  return (
    <div style={{ fontFamily: "var(--font-sans)", ...style }}>
      {view === "summary" && summary && (
        <SummaryView
          summary={summary}
          onRuleClick={openRule}
          onViewAll={() => setView("list")}
        />
      )}
      {view === "list" && (
        <RuleListView
          entityId={entityId}
          onRuleClick={openRule}
          onBack={() => setView("summary")}
        />
      )}
      {view === "detail" && detail && (
        <RuleDetailView
          detail={detail}
          onClose={() => setView("summary")}
          onSiblingClick={openRuleById}
        />
      )}
    </div>
  );
}

export default DQRulesReport;